import { Component } from 'react';
import { AlertTriangle, RefreshCcw } from 'lucide-react';
// eslint-disable-next-line no-unused-vars
import { motion } from 'framer-motion';

// Components
import Button from '@/shared/components/elements/Button.jsx';
import Logo from '@/shared/components/modules/Logo.jsx';

// Assets
import logo from '@/assets/images/Logo.png';
import whiteLogo from '@/assets/images/WhiteLogo.png';

const pageVariants = {
  hidden: { opacity: 0, y: 15 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: 'easeOut',
      when: 'beforeChildren',
      staggerChildren: 0.15,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
};

class GlobalErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });

    if (import.meta.env.DEV) {
      console.error('Error Boundary Caught an Error:', error, errorInfo);
    }
    // TODO: Send error to Sentry or other monitoring tools
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <motion.div
          variants={pageVariants}
          initial="hidden"
          animate="visible"
          className="fixed inset-0 z-[9999] flex flex-col overflow-y-auto bg-gray-50/95 backdrop-blur-3xl select-none dark:bg-[#111322]/95"
        >
          {/* Header */}
          <header className="flex w-full items-center justify-between px-6 py-5 sm:px-10">
            <div className="flex-1" />

            <div className="flex flex-1 justify-center">
              <Logo
                src={logo}
                darkSrc={whiteLogo}
                alt="لوگو"
                imageClassName="h-10 w-auto object-contain sm:h-12"
                onClick={() => {
                  window.location.href = '/';
                }}
              />
            </div>

            <div className="flex-1" />
          </header>

          {/* Main Content */}
          <main className="flex flex-1 flex-col items-center justify-center px-4 py-10 sm:px-6">
            <div className="w-full max-w-2xl text-center">
              {/* Error Icon */}
              <motion.div
                variants={itemVariants}
                className="mx-auto mb-8 flex h-24 w-24 items-center justify-center rounded-full bg-red-100/50 text-red-500 dark:bg-red-500/10 dark:text-red-400"
              >
                <AlertTriangle size={48} strokeWidth={2} />
              </motion.div>

              {/* Texts */}
              <motion.div variants={itemVariants} className="mb-10">
                <h1 className="mb-4 text-2xl font-bold text-gray-900 sm:text-3xl lg:text-4xl dark:text-white">
                  متأسفانه خطای غیرمنتظره‌ای رخ داد!
                </h1>
                <p className="mx-auto max-w-lg text-lg leading-relaxed text-gray-600 dark:text-gray-400">
                  در پردازش این بخش از سیستم مشکلی پیش آمده است. لطفاً صفحه را
                  بارگذاری مجدد کنید یا دقایقی دیگر مجدداً تلاش نمایید.
                </p>
              </motion.div>

              {/* Action Button */}
              <motion.div
                variants={itemVariants}
                className="flex justify-center"
              >
                <Button
                  variant="primary"
                  size="xl"
                  onClick={this.handleReload}
                  className="min-w-[280px] text-lg font-bold shadow-[0_8px_25px_rgba(239,89,38,0.35)] transition-transform hover:-translate-y-1 sm:min-w-[320px]"
                >
                  <RefreshCcw size={20} className="ml-2 inline-block" />
                  بارگذاری مجدد صفحه
                </Button>
              </motion.div>

              {/* Developer Error Details (Only in DEV mode) */}
              {import.meta.env.DEV && this.state.error && (
                <motion.div
                  variants={itemVariants}
                  className="mt-12 overflow-hidden rounded-3xl border border-red-500/20 bg-red-50/50 p-6 text-left dark:border-red-500/10 dark:bg-red-950/20"
                  dir="ltr"
                >
                  <p className="mb-2 font-bold text-red-600 dark:text-red-400">
                    Developer Error Details:
                  </p>
                  <pre className="custom-scrollbar overflow-x-auto font-mono text-sm whitespace-pre-wrap text-red-800 dark:text-red-300">
                    {this.state.error.toString()}
                    {'\n'}
                    {this.state.errorInfo?.componentStack}
                  </pre>
                </motion.div>
              )}
            </div>
          </main>
        </motion.div>
      );
    }

    return this.props.children;
  }
}

export default GlobalErrorBoundary;
